import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { IndexResponse } from '@api/models/response.model';
import { RequestsService } from '@api/services/requests.service';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { RequestList } from '@api/models/request.model';
import { effect } from '@angular/core';
import { AppService } from '../../app.service';
import { ButtonComponent } from '@ui/button/button.component';
import { CardComponent } from '@ui/card/card.component';
import { CreateRequestModalComponent } from './create-request-modal/create-request-modal.component';
import { ModalService } from '@ui/modal/modal.service';
import { Router } from '@angular/router';
import { InputComponent } from '@ui/input/input.component';
import { SelectComponent } from '@ui/select/select.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '@ui/notification/notification.service';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'app-requests',
  standalone: true,
  imports: [
    TableComponent,
    CardComponent,
    ButtonComponent,
    InputComponent,
    SelectComponent,
    CommonModule,
    FormsModule,
  ],
  template: ` <div
    class="flex flex-col gap-4 h-[calc(100vh-theme(spacing.24))] overflow-hidden"
  >

    <ui-card>
    <div class="flex justify-end items-center">
        <ui-button
          [icon]="'add'"
          (onClick)="addRequest()"
          [disabled]="!municipalityId()"
          >Nieuwe terugname aanvraag</ui-button
        >
      </div>
      <div class="flex flex-col gap-4 justify-between">
        <form (ngSubmit)="applyFilters()">
          <div class="grid grid-cols-3 md:grid-cols-3 gap-4">
            <ui-input
              label="WMO referentie"
              placeholder="Zoek op referentie..."
              [(ngModel)]="filters.wmoReference"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onFilterChange()"
            ></ui-input>

            <!-- <ui-input
              label="Status"
              placeholder="Zoek op status..."
              [(ngModel)]="filters.status"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onFilterChange()"
            ></ui-input> -->

            <ui-select
              label="Status"
              [placeholder]="'Selecteer een status'"
              [options]="statusOptions()"
              [(ngModel)]="filters.status"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onStatusChange($event)"
            ></ui-select>

            <div class="flex gap-2 items-end" style="padding-bottom: 2px;">
              <ui-button variant="primary" [icon]="'filter'" type="submit"
                >Filter toepassen</ui-button
              >
              <ui-button
                variant="ghost"
                [icon]="'refresh'"
                type="button"
                (onClick)="resetFilters()"
                >Reset filters</ui-button
              >
            </div>

            <ui-input
              label="Toon BO Afgeronde en geannuleerde aanvragen"
              type="checkbox"
              [(ngModel)]="filters.showClosed"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="applyFilters()"
            ></ui-input>
          </div>
        </form>
      </div>
    </ui-card>
    <div class="h-full overflow-hidden">
      <ui-table
        [columns]="columns"
        [data]="requests()"
        [loading]="loading()"
        (onRowClick)="onRowClick($event)"
        (onPageChange)="onPageChange($event)"
        [showPagination]="true"
        [meta]="meta()"
        [bordered]="false"
      ></ui-table>
    </div>
  </div>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestsComponent {
  requestsService = inject(RequestsService);
  modalService = inject(ModalService);
  appService = inject(AppService);
  router = inject(Router);
  notificationService = inject(NotificationService);

  requests = signal<RequestList[]>([]);
  loading = signal<boolean>(false);
  meta = signal<IndexResponse<RequestList>['meta'] | null>(null);
  statuses = signal<{status: string}[]>([]);
  statusOptions = computed(() => this.statuses().map((status) => ({
    label: new TitleCasePipe().transform(status.status),
    value: status.status
  })));
  filters = {
    wmoReference: '',
    status: '',
    showClosed: false,
  };

  municipalityId = this.appService.municipalityId;

  columns: TableColumn[] = [
    { key: 'wmo_reference', label: 'WMO referentie' },
    {
      key: 'status',
      label: 'Status',
      format: (value: {status: string}) =>
        new TitleCasePipe().transform(value.status),
    },
    {
      key: 'user_name',
      label: 'Aangemaakt door',
    },
    {
      key: 'created_at',
      label: 'Aangemaakt op',
      format: (value: string) =>
        new Date(value).toLocaleString('nl-NL', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
      align: 'right',
    },
    {
      key: 'updated_at',
      label: 'Laatst aangepast op',
      format: (value: string) =>
        new Date(value).toLocaleString('nl-NL', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
      align: 'right',
    },
  ];

  constructor() {
    effect(
      () => {
        if (this.appService.municipalityId()) {
          this.fetchRequests();
          this.getStatuses();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.fetchRequests();
  }

  fetchRequests(
    page: number = 1,
    perPage: number = 50,
    q: string = '',
    filters: any = {}
  ) {
    this.loading.set(true);
    try {
      const withdrawalFilters = { ...filters, type: 'withdrawal' };
      this.requestsService
        .getRequests(page, perPage, q, withdrawalFilters)
        .subscribe((requests) => {
          this.requests.set(requests.data);
          this.meta.set(requests.meta);
          this.loading.set(false);
        });
    } catch (error: any) {
      this.notificationService.error(error.message);
      this.loading.set(false);
    }
  }

  getStatuses() {
    this.requestsService.getStatuses().subscribe((statuses) => {
      this.statuses.set(statuses);
    });
  }

  addRequest() {
    const modalRef = this.modalService.open(CreateRequestModalComponent, {
      data: { initialRequestType: 'withdrawal' as const },
    });
    modalRef.onClose.subscribe(() => {
      this.fetchRequests();
    });
  }

  onRowClick(event: any) {
    this.router.navigate(['/requests', event.id]);
  }

  onPageChange(page: number) {
    this.fetchRequests(page, 50, this.filters.wmoReference, this.filters);
  }

  onFilterChange() {}

  onStatusChange(event: any) {
    this.filters.status = event;
    this.fetchRequests(1, 50, this.filters.wmoReference, this.filters);
  }

  applyFilters() {
    this.fetchRequests(1, 50, this.filters.wmoReference, this.filters);
  }

  resetFilters() {
    this.filters.wmoReference = '';
    this.filters.status = '';
    this.filters.showClosed = false;
    this.fetchRequests(1, 50, '');
  }
}
